// @ts-nocheck
import React, { useState, useEffect } from "react";
import IframeRoutes from "./routes/IframeRoutes";
import { useLocation, useHistory } from "react-router-dom";
import ErrorHandler from "components/common/ErrorHandler";
import { errorState } from "recoil/error/atom";
import { modalState } from "recoil/modal/atom";
import ModalComponent from "components/common/ModalComponent";
import { toastMessageState } from "recoil/toast/atom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { setcompanyDetails } from "recoil/companyDetails/selector";
import { getEmployeeAuthorisation } from "swr/auth";
import { setCurrentOrganisationDetails } from "recoil/organisations/selector";
import { setDocumentCategories } from "recoil/documentCategories/selector";
import { setNoteTypes } from "recoil/noteTypes/selector";
import { ToastHandler } from "qjumpers.common.ui";
import { jwtDecode } from 'jwt-decode';
import { getRulesDetails } from "utils/authHelper";
import LoadingWidget from "./widgets/loader";
import configDetails from "constants/config";
import { AuthProvider, useAuth } from "react-oidc-context";
import { setTokenProvider } from "./middleware/api";
import { loadDocumentCategoriesByOrg } from "swr/documentcategory";
import { loadNoteTypes } from "swr/notes";
import 'react-toastify/dist/ReactToastify.css';
import { RecoilRoot } from "recoil";

// Update your IframeApp with immediate token setting
function IframeApp(props: any) {
  const history = useHistory();
  const location = useLocation();
  const error = useRecoilValue(errorState);
  const modal = useRecoilValue(modalState);
  const toast = useRecoilValue(toastMessageState);
  const setToast = useSetRecoilState(toastMessageState);
  const auth = useAuth();
  
  // Simplified state
  const [isAuthorized, setIsAuthorized] = useState<any>(null);
  const [currentCompanyDetails, setCurrentCompanyDetails] = useState<any>();
  const [employeeAuthorization, setEmployeeAuthorizationState] = useState<any>(null);
  const [rulesDetails, setRulesDetails] = useState<any>();
  const [dataLoaded, setDataLoaded] = useState<any>(false);
  
  // Recoil setters
  const setCurrentOrgValue = useSetRecoilState(setCurrentOrganisationDetails);
  const setCompanyDetailsState = useSetRecoilState(setcompanyDetails);
  const setDocumentCategoriesData = useSetRecoilState(setDocumentCategories);
  const setNoteTypesData = useSetRecoilState(setNoteTypes);

  // Simplified authentication listener
  useEffect(() => {
    const handleTokenMessage = async (event) => {
      const queryParams = new URLSearchParams(location.search);
      let token = queryParams.get("jwt") || event.data?.access_token;

      if (!token) {
        return;
      }

      console.log("Token received, validating and setting up...");
      
      try {
        // Validate token and get company details
        const response = await fetch(
          `${configDetails.ADMIN.apiHost}${configDetails.ADMIN.apiPath}/validate/token`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token })
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        if (data.tokens && data.organisation) {
          const { tokens, organisation: currentCompany } = data;
          
          // ✅ Set token provider IMMEDIATELY - this is crucial
          await setTokenProvider(() => {
            access_token: tokens.access_token;
            expires_at: Date.now() + tokens.expires_in * 1000;
          });
          console.log("Token provider set - API calls will now work");
          
          // Store for OIDC context
          // localStorage.setItem("qjumpers.adminui.auth.token", tokens.access_token);
          localStorage.setItem("company-id", currentCompany.id);
          localStorage.setItem("company-name", currentCompany.accountingName);
          
          // Create and store OIDC user
          const decodedToken = jwtDecode(tokens.access_token);
          const oidcUser = {
            access_token: tokens.access_token,
            refresh_token: tokens.refresh_token,
            expires_at: decodedToken.exp,
            profile: {
              sub: decodedToken.sub,
              email: decodedToken.email,
              name: decodedToken.name,
              company_id: currentCompany.id,
              company_name: currentCompany.accountingName
            }
          };
          
          const userStorageKey = `oidc.user:${configDetails.KEYCLOAK.apiHost}${configDetails.KEYCLOAK.apiPath}/realms/${configDetails.realm}/:qjumpers`;
          sessionStorage.setItem(userStorageKey, JSON.stringify(oidcUser));
          
          // Set company details
          setCurrentCompanyDetails(currentCompany);
          setCurrentOrgValue(currentCompany);
          setCompanyDetailsState(currentCompany);
          
          setIsAuthorized(true);
          console.log("Authentication and token setup completed");
        } else {
          setIsAuthorized(false);
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        setIsAuthorized(false);
      }
    };

    window.addEventListener('message', handleTokenMessage);
    handleTokenMessage({}); // Check URL params immediately
    
    return () => window.removeEventListener('message', handleTokenMessage);
  }, [location.search]);

  // Load data after auth is ready
  const [authLoading, setAuthLoading] = useState<any>(false);
  const [dataLoading, setDataLoading] = useState<any>(false);

  useEffect(() => {
    const loadData = async () => {
      // Wait for basic auth and company details first
      if (!isAuthorized || !currentCompanyDetails) {
        console.log("Waiting for authorization and company details...");
        return;
      }

      // Don't wait for auth.user.access_token - we have the token from validation
      if (dataLoaded) {
        console.log("Data already loaded, skipping...");
        return;
      }

      try {
        console.log("Starting data loading process...");
        
        // Load employee authorization first
        console.log("Loading employee authorization...");
        const authResult = await getEmployeeAuthorisation(currentCompanyDetails.id);
        if (authResult?.data) {
          setEmployeeAuthorizationState(authResult.data);
          console.log("Employee authorization loaded:");
        } else {
          console.warn("No employee authorization data received");
          // Set empty object to prevent blocking
          setEmployeeAuthorizationState({});
        }

        // Load document categories and note types in parallel
        console.log("Loading document categories and note types...");
        const [docCategories, noteTypes] = await Promise.all([
          loadDocumentCategoriesByOrg(currentCompanyDetails.id).catch(error => {
            console.error("Failed to load document categories:", error);
            return [];
          }),
          loadNoteTypes(currentCompanyDetails.id).catch(error => {
            console.error("Failed to load note types:", error);
            return [];
          })
        ]);

        setDocumentCategoriesData(docCategories);
        setNoteTypesData({ data: noteTypes });
        console.log("Document categories and note types loaded");
        
        // Mark data as loaded
        setDataLoaded(true);
        console.log("All data loading completed");

      } catch (error) {
        console.error("Failed to load data:", error);
        // Set fallback data to prevent infinite loading
        setEmployeeAuthorizationState({});
        setDocumentCategoriesData({ data: [] });
        setNoteTypesData({ data: [] });
        setDataLoaded(true);
      }
    };

    loadData();
  }, [isAuthorized, currentCompanyDetails, dataLoaded]); // ✅ Remove auth dependency

  // Set rules when all data is ready
  useEffect(() => {
    if (currentCompanyDetails && employeeAuthorization !== null && !rulesDetails) {
      console.log("Setting rules details...");
      try {
        const rules = getRulesDetails(currentCompanyDetails, currentCompanyDetails, employeeAuthorization || {});
        setRulesDetails(rules);
        console.log("Rules details set:");
      } catch (error) {
        console.error("Failed to set rules details:", error);
        // Set fallback rules
        setRulesDetails({ companyDetails: currentCompanyDetails });
      }
    }
  }, [currentCompanyDetails, employeeAuthorization, rulesDetails]);

  const clearToastMessage = () => setToast([]);

  const isAppReady = employeeAuthorization !== null && rulesDetails && dataLoaded;

  return (
    <div className="App">
      <IframeTokenProvider>
        {isAuthorized ? (
          <>
            <ErrorHandler errors={error} />
            <ModalComponent modalData={modal} />
            <ToastHandler toast={toast} clearToasts={clearToastMessage} />
            {isAppReady ? (
              <IframeRoutes rulesDetails={rulesDetails} />
            ) : (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                <LoadingWidget color="grey" />
                <div>
                  Loading application data...
                  {dataLoading && <div>Fetching data...</div>}
                </div>
              </div>
            )}
          </>
        ) : isAuthorized === null ? (
          <div style={{ textAlign: "center", padding: "5rem" }}>
            <LoadingWidget color="grey" />
            <div>Authenticating user...</div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "5rem" }}>
            <div>Unauthorized</div>
          </div>
        )}
      </IframeTokenProvider>
    </div>
  );
}

// Simplified TokenProvider - just for backup
const IframeTokenProvider = ({ children  }: any) => {
  const auth = useAuth();
  
  React.useEffect(() => {
    // This is now backup - token should already be set in handleTokenMessage
    if (auth?.user?.access_token) {
      console.log("Backup: Updating token provider from auth.user", auth?.user);
      
      setTokenProvider(() => {
        if (!auth?.user) {
          return null;
        }
        return {
          access_token: auth.user.access_token,
          expires_at: auth.user.expires_at,
          renew: async () => {
            // Forces silent renew via react-oidc-context
            await auth.signinSilent?.();
            return auth.user;
          },
          logout: async (options = {}) => {
            try {
              if (options.silent && auth.signoutSilent) {
                await auth.signoutSilent();
              } else {
                await auth.signoutRedirect();
              }
            } catch (e) {
              console.error("Logout failed", e);
            }
          },
        };
      });
    }
  }, [auth.user, auth.signinSilent]);
  
  return children;
};

// Simplified wrapper
const IframeAuthWrapper = (props: any) => {
  const oidcConfig = {
    authority: `${configDetails.KEYCLOAK.apiHost}${configDetails.KEYCLOAK.apiPath}/realms/${configDetails.realm}/`,
    client_id: `${configDetails.realm}`,
    redirect_uri: window.location.origin,
    response_type: "code",
    automaticSilentRenew: false,
    loadUserInfo: false,
    monitorSession: false,
  };

  const onSignoutCallback = () => {
    console.log("Sign-out successful");
 
    // Only clear application-specific data
    sessionStorage.clear();
  };

  return (
    <RecoilRoot>
      <AuthProvider {...oidcConfig} onSignoutCallback={onSignoutCallback}>
        <IframeApp {...props} />
      </AuthProvider>
    </RecoilRoot>
  );
};

export default IframeAuthWrapper;